import {
  ApartmentOutlined,
  CallMadeOutlined,
  CallReceivedOutlined,
  Construction,
  InsertChart,
  NoteAddOutlined,
  PieChartOutlineOutlined,
  Receipt,
  ShowChart,
} from '@mui/icons-material';
import { SIDE_STACK, TXM_REFERRALS_PATH } from '@/constants';
import { CanvaIcon, CanvaIconDisabled } from './CanvaIcon';
import { CashIcon } from './CashIcon';
import { FolderIcon } from './FolderIcon';
import { type QuickLink, QuickLinkName } from './types';
import { YardSignIcon } from './YardSignIcon';

export const QUICK_LINKS_MAP: Record<string, QuickLink> = {
  [QuickLinkName.CREATE_LEASE]: {
    isInternalLink: true,
    href: '/leases/new',
    title: 'Create a New Lease',
    iconLarge: <ApartmentOutlined fontSize="large" />,
    iconMedium: <ApartmentOutlined sx={{ width: 30, height: 30 }} />,
    iconSmall: <ApartmentOutlined fontSize="small" />,
  },
  [QuickLinkName.CREATE_LISTING]: {
    isInternalLink: true,
    href: '/listings/new',
    title: 'Create a New Listing',
    iconLarge: <YardSignIcon />,
    iconMedium: <YardSignIcon width={30} />,
    iconSmall: <YardSignIcon width={20} />,
  },
  [QuickLinkName.CREATE_OFFER]: {
    isInternalLink: true,
    href: '/offers/new',
    title: 'Create a New Offer',
    iconLarge: <CashIcon />,
    iconMedium: <CashIcon width={30} />,
    iconSmall: <CashIcon width={20} />,
  },
  [QuickLinkName.BUYER_AGREEMENT]: {
    isInternalLink: true,
    href: '/buyers/new',
    title: 'Create Buyer Agreement',
    iconLarge: <NoteAddOutlined fontSize="large" />,
    iconMedium: <NoteAddOutlined sx={{ width: 30, height: 30 }} />,
    iconSmall: <NoteAddOutlined fontSize="small" />,
  },
  [QuickLinkName.START_REFERRAL]: {
    isInternalLink: true,
    href: `${TXM_REFERRALS_PATH}/new?referralType=sending`,
    title: 'Give Client Referral',
    iconLarge: <CallMadeOutlined fontSize="large" />,
    iconMedium: <CallMadeOutlined sx={{ width: 30, height: 30 }} />,
    iconSmall: <CallMadeOutlined fontSize="small" />,
  },
  [QuickLinkName.RECEIVE_REFERRAL]: {
    isInternalLink: true,
    href: `${TXM_REFERRALS_PATH}/new?referralType=receiving`,
    title: 'Receive Client Referral',
    iconLarge: <CallReceivedOutlined fontSize="large" />,
    iconMedium: <CallReceivedOutlined sx={{ width: 30, height: 30 }} />,
    iconSmall: <CallReceivedOutlined fontSize="small" />,
  },
  [QuickLinkName.GROWTH_HUB]: {
    isInternalLink: false,
    href: SIDE_STACK,
    title: 'Side Stack & Marketplace',
    iconLarge: <Construction fontSize="large" />,
    iconMedium: <Construction sx={{ width: 30, height: 30 }} />,
    iconSmall: <Construction fontSize="small" />,
  },
  [QuickLinkName.GROWTH_REPORT]: {
    isInternalLink: true,
    href: '/reports/?report=665421',
    title: 'View Growth Report',
    iconLarge: <ShowChart fontSize="large" />,
    iconMedium: <ShowChart sx={{ width: 30, height: 30 }} />,
    iconSmall: <ShowChart fontSize="small" />,
  },
  [QuickLinkName.FINANCIAL_REPORT]: {
    isInternalLink: true,
    // The report ID for financial reports is different depending on the
    // user's role. But since the financial report is always the first in
    // the reports list, it's the default report when the user is placed
    // on the reports page.
    href: '/reports',
    title: 'View Financial Report',
    iconLarge: <PieChartOutlineOutlined fontSize="large" />,
    iconMedium: <PieChartOutlineOutlined sx={{ width: 30, height: 30 }} />,
    iconSmall: <PieChartOutlineOutlined fontSize="small" />,
  },
  [QuickLinkName.PLATFORM_FEE_REPORT]: {
    isInternalLink: true,
    href: '/reports/?report=1143867',
    title: 'View Platform Fee Report',
    iconLarge: <Receipt fontSize="large" />,
    iconMedium: <Receipt sx={{ width: 30, height: 30 }} />,
    iconSmall: <Receipt fontSize="small" />,
  },
  [QuickLinkName.PRICING_PLAN_PROGRESS_REPORT]: {
    isInternalLink: true,
    href: '/reports/?report=1131598',
    title: 'View Pricing Plan Progress',
    iconLarge: <InsertChart fontSize="large" />,
    iconMedium: <InsertChart sx={{ width: 30, height: 30 }} />,
    iconSmall: <InsertChart fontSize="small" />,
  },
  [QuickLinkName.USER_TEMPLATES]: {
    isInternalLink: true,
    href: '/templates/new',
    title: 'Create a Template',
    iconLarge: <FolderIcon />,
    iconMedium: <FolderIcon width={30} />,
    iconSmall: <FolderIcon width={20} />,
  },
  [QuickLinkName.CANVA]: {
    isInternalLink: false,
    href: 'https://www.canva.com/',
    title: 'Canva',
    iconLarge: <CanvaIcon />,
    iconMedium: <CanvaIcon width={30} />,
    iconSmall: <CanvaIcon width={20} />,
    iconMediumDisabled: <CanvaIconDisabled width={30} />,
    iconSmallDisabled: <CanvaIconDisabled width={20} />,
  },
};

export const buildQuickLinks = (quickLinks: QuickLinkName[]): QuickLink[] => {
  return quickLinks
    .map((link) => QUICK_LINKS_MAP[link] ?? null)
    .filter((link) => !!link);
};
